import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, FileText, Download, ChevronRight, ChevronLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../../../services/apiClient';
import { patientService } from '../services/patientService';
import toast from 'react-hot-toast';
import BookingDetailsPopup from '../components/BookingDetailsPopup';
import CancelBookingPopup from '../components/CancelBookingPopup';
import EditBookingPopup from '../components/EditBookingPopup';
import HomeCareBookingSection from '../../../components/HomeCareBookingSection';
import { useNotifications } from '../../../context/NotificationContext';
import { NotificationList, NotificationDetailDialog } from '../../../components/Notifications';

const PatientDashboardPage = () => {
  const navigate = useNavigate();
  
  // Notification context
  const { 
    notifications: contextNotifications, 
    unreadCount, 
    loading: notificationsLoading,
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    refresh: refreshNotifications 
  } = useNotifications();
  
  // Selected notification for detail dialog
  const [selectedNotification, setSelectedNotification] = useState(null);
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient Home';
  }, []);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookingForm, setBookingForm] = useState({
    reason: '',
    appointmentType: '',
    scheduleType: '',
    specialty: '',
    doctor: '',
    doctor_id: '',
    clinicLocation: '',
    clinicianId: '',
    clinicianName: ''
  });

  // New state for specialties and doctors
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // State for clinics
  const [availableClinics, setAvailableClinics] = useState([]);
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);

  // State for clinicians
  const [clinicians, setClinicians] = useState([]);
  const [loadingClinicians, setLoadingClinicians] = useState(false);

  // State for patient data (includes patient_type for Home Care)
  const [patientData, setPatientData] = useState(null);
  const [loadingPatientData, setLoadingPatientData] = useState(false);

  // Mock data for notifications
  const notifications = [
    { id: 1, text: 'Please schedule follow up with Dr Michael Barnes', checked: false },
    { id: 2, text: 'Pathology Results Received', checked: false }
  ];

  // Mock data for accounts
  const accounts = [
    { id: 1, date: '16/09/2024', type: 'Telehealth', icon: <Download className="h-4 w-4" /> },
    { id: 2, date: '15/09/2024', type: 'Telehealth', icon: <Download className="h-4 w-4" /> },
    { id: 3, date: '14/09/2024', type: 'Telehealth', icon: <Download className="h-4 w-4" /> },
    { id: 4, date: '13/09/2024', type: 'Telehealth', icon: <Download className="h-4 w-4" /> }
  ];

  // Mock data for FAQs
  const faqs = [
    'How do I change my details?',
    'How do I make a booking?',
    'How do I change a booking?',
    'How do I contact my doctor?'
  ];

  // Mock data for latest news
  const news = [
    {
      id: 1,
      image: '/news-thumbnail-1.jpg',
      title: 'News Title',
      excerpt: 'Paragraph Excerpt'
    },
    {
      id: 2,
      image: '/news-thumbnail-2.jpg',
      title: 'News Title',
      excerpt: 'Paragraph Excerpt'
    }
  ];

  // State for real upcoming bookings data
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loadingUpcomingBookings, setLoadingUpcomingBookings] = useState(false);

  // Fetch patient data, specialties and upcoming bookings on component mount
  useEffect(() => {
    fetchPatientData();
    fetchSpecialties();
    fetchUpcomingBookings();
  }, []);

  // Fetch doctors when specialty changes
  useEffect(() => {
    if (bookingForm.specialty) {
      fetchDoctorsBySpecialty(bookingForm.specialty);
    } else {
      setDoctors([]);
      setBookingForm(prev => ({ ...prev, doctor: '' }));
    }
  }, [bookingForm.specialty]);

  // Set first doctor as default when doctors are loaded
  useEffect(() => {
    if (doctors.length > 0 && !bookingForm.doctor) {
      const firstDoctor = doctors[0];
      setBookingForm(prev => ({ 
        ...prev, 
        doctor: `${firstDoctor.firstname} ${firstDoctor.lastname}`,
        doctor_id: firstDoctor.doctor_id,
        clinicLocation: '' // Will be set after fetching clinics
      }));
    }
  }, [doctors]);

  // Load all available clinics on component mount (independent of doctor selection)
  useEffect(() => {
    const fetchAllClinics = async () => {
      try {
        setLoadingClinics(true);
        const clinics = await patientService.getAllClinics();
        setAvailableClinics(clinics);
        
        // Don't auto-set clinic to avoid potential state conflicts - let user select
      } catch (error) {
        console.error('Error fetching all clinics:', error);
        setAvailableClinics([]);
      } finally {
        setLoadingClinics(false);
      }
    };
    
    fetchAllClinics();
  }, []); // Only run once on mount

  // Fetch doctor availability when doctor changes (including auto-population)
  useEffect(() => {
    if (bookingForm.doctor_id) {
      fetchDoctorMonthAvailability(currentMonth);
    } else {
      // Clear calendar availability when no doctor is selected
      setDoctorMonthAvailability(null);
    }
  }, [bookingForm.doctor_id, currentMonth]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDoctorDropdown && !event.target.closest('.doctor-dropdown-container')) {
        setShowDoctorDropdown(false);
      }
      if (showClinicDropdown && !event.target.closest('.clinic-dropdown-container')) {
        setShowClinicDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDoctorDropdown, showClinicDropdown]);

  const fetchPatientData = async () => {
    try {
      setLoadingPatientData(true);
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (userData.id) {
        const patient = await patientService.getPatientByUserId(userData.id);
        setPatientData(patient);
        console.log('👤 Patient data loaded:', {
          id: patient.id,
          patient_type: patient.patient_type,
          name: `${patient.first_name} ${patient.last_name}`
        });
      }
    } catch (error) {
      console.error('❌ Error loading patient data:', error);
    } finally {
      setLoadingPatientData(false);
    }
  };

  const fetchSpecialties = async () => {
    try {
      setLoadingSpecialties(true);
      const specialtiesData = await patientService.getSpecialties();
      setSpecialties(specialtiesData);
    } catch (error) {
      console.error('Error fetching specialties:', error);
      
      // Check if it's an authentication error
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        console.log('🔐 Authentication error detected. Please log in again.');
        // Clear invalid tokens
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        // Redirect to login or show login prompt
        window.location.href = '/login';
        return;
      }
      
      // Fallback to default specialties if API fails
      setSpecialties(['Cardiology', 'Internal Medicine', 'Pediatrics', 'Family Medicine', 'Orthopedics', 'Sports Medicine', 'Dermatology', 'Cosmetic Surgery', 'Neurology', 'Psychiatry']);
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const fetchDoctorsBySpecialty = async (specialty) => {
    try {
      setLoadingDoctors(true);
      const doctorsData = await patientService.getDoctorsBySpecialty(specialty);
      setDoctors(doctorsData);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      
      // Check if it's an authentication error
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        console.log('🔐 Authentication error detected. Please log in again.');
        // Clear invalid tokens
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        // Redirect to login or show login prompt
        window.location.href = '/login';
        return;
      }
      
      setDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchUpcomingBookings = async () => {
    try {
      setLoadingUpcomingBookings(true);
      
      // Fetch all bookings and filter for upcoming ones
      const allBookings = await patientService.getUserBookings();
      
      // Get today's date for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Filter for upcoming bookings (future date and scheduled status)
      const upcomingBookings = allBookings.filter(booking => {
        const bookingDate = new Date(booking.appointment_date);
        bookingDate.setHours(0, 0, 0, 0);
        return bookingDate >= today && booking.status === 'Scheduled';
      });

      // Sort by date (earliest first) and take only the first one
      upcomingBookings.sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
      setUpcomingBookings(upcomingBookings.slice(0, 1));
      
    } catch (error) {
      console.error('Error fetching upcoming bookings:', error);
      setUpcomingBookings([]);
      
      // Handle 401 unauthorized error
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        console.log('🔐 Authentication error detected. Please log in again.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }
    } finally {
      setLoadingUpcomingBookings(false);
    }
  };

  const handleSpecialtyChange = (specialty) => {
    setBookingForm(prev => ({ 
      ...prev, 
      specialty: specialty,
      doctor: '' // Reset doctor when specialty changes
    }));
    setShowDoctorDropdown(false);
  };

  const handleDoctorSelect = async (doctor) => {
    try {
      // Update doctor information (clinics are already loaded globally)
      setBookingForm(prev => ({ 
        ...prev, 
        doctor: `${doctor.firstname} ${doctor.lastname}`,
        doctor_id: doctor.doctor_id
        // Keep current clinic selection - no need to reset
      }));

      setShowDoctorDropdown(false);
      // Clear previously selected date/time and times list
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableTimes([]);
      
      // Fetch month availability for calendar color coding
      await fetchDoctorMonthAvailability(currentMonth);
    } catch (error) {
      console.error('Error handling doctor selection:', error);
    }
  };

  const toggleDoctorDropdown = () => {
    if (doctors.length > 0) {
      setShowDoctorDropdown(!showDoctorDropdown);
    }
  };

  const handleClinicSelect = (clinic) => {
    setBookingForm(prev => ({
      ...prev,
      clinicLocation: clinic.clinic_name,
      clinicianId: '',  // Reset clinician when clinic changes
      clinicianName: ''
    }));
    setShowClinicDropdown(false);
    // Fetch clinicians for the selected clinic
    fetchCliniciansByClinic(clinic.clinic_name);
  };

  const toggleClinicDropdown = () => {
    console.log(`🏥 Patient Toggle Clinic Dropdown: availableClinics=${availableClinics.length}, appointmentType=${bookingForm.appointmentType}, showClinicDropdown=${showClinicDropdown}`);
    if (availableClinics.length > 0) {
      setShowClinicDropdown(!showClinicDropdown);
    }
  };

  // Fetch clinicians when clinic location changes
  const fetchCliniciansByClinic = async (clinicName) => {
    if (!clinicName) {
      setClinicians([]);
      return;
    }

    try {
      setLoadingClinicians(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/clinicians/by-clinic-name/${encodeURIComponent(clinicName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch clinicians');
      }

      const data = await response.json();
      setClinicians(data);
      console.log(`👨‍⚕️ Fetched ${data.length} clinicians for clinic: ${clinicName}`);
    } catch (error) {
      console.error('Error fetching clinicians:', error);
      toast.error('Failed to load clinicians for selected clinic');
      setClinicians([]);
    } finally {
      setLoadingClinicians(false);
    }
  };

  const handleClinicianSelect = (clinicianId) => {
    const selectedClinician = clinicians.find(c => c.id === parseInt(clinicianId));
    if (selectedClinician) {
      setBookingForm(prev => ({
        ...prev,
        clinicianId: clinicianId,
        clinicianName: selectedClinician.full_name || `${selectedClinician.firstname} ${selectedClinician.lastname}`.trim()
      }));
      console.log(`👨‍⚕️ Selected clinician: ${selectedClinician.full_name} (Clinician ID: ${clinicianId}, User ID: ${selectedClinician.user_id})`);
    }
  };

  // Backend-driven available times for selected doctor/date
  const [availableTimes, setAvailableTimes] = useState([]);
  
  // Doctor availability for the month (for calendar color coding)
  const [doctorMonthAvailability, setDoctorMonthAvailability] = useState(null);

  // Filter past time slots for today's date
  const filterPastSlotsForToday = (times, selectedDate) => {
    // Only filter if the selected date is today
    const today = new Date();
    const selected = new Date(selectedDate);
    
    // Check if selected date is today (compare date only)
    const isToday = today.toDateString() === selected.toDateString();
    
    if (!isToday) {
      return times; // Return all times for non-today dates
    }
    
    // For today, filter out past time slots
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    return times.filter(time => {
      if (!time) return false;
      
      const [slotHour, slotMinute] = time.split(':').map(Number);
      const slotTimeInMinutes = slotHour * 60 + slotMinute;
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      // Keep slots that start after current time
      return slotTimeInMinutes > currentTimeInMinutes;
    });
  };

  const fetchDoctorSlots = async (dateObj) => {
    try {
      setAvailableTimes([]);
      if (!bookingForm.doctor_id) return;
      const doctor = await patientService.getDoctorById(bookingForm.doctor_id); // get numeric id
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, '0');
      const d = String(dateObj.getDate()).padStart(2, '0');
      const iso = `${y}-${m}-${d}`;
      const slots = await patientService.getDoctorDaySlots(doctor.id, iso);
      const times = (slots || [])
        .filter((s) => s.status === 'AVAILABLE')
        .map((s) => s.start_time.slice(0,5));
      
      // Filter out past time slots for today
      const filteredTimes = filterPastSlotsForToday(times, dateObj);
      setAvailableTimes(filteredTimes);
    } catch (e) {
      toast.error(e.message || 'Failed to load doctor slots');
      setAvailableTimes([]);
    }
  };

  const fetchDoctorMonthAvailability = async (monthDate) => {
    try {
      if (!bookingForm.doctor_id) {
        setDoctorMonthAvailability(null);
        return;
      }
      
      const doctor = await patientService.getDoctorById(bookingForm.doctor_id);
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      
      const availability = await patientService.getDoctorMonthAvailability(doctor.id, year, month);
      setDoctorMonthAvailability(availability);
    } catch (e) {
      console.error('Failed to load doctor month availability:', e);
      setDoctorMonthAvailability(null);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatTimeSlot = (timeSlot) => {
    const [hours, minutes] = timeSlot.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Function to format display values for confirmation block
  const formatDisplayValue = (field, value) => {
    const formatMap = {
      appointmentType: {
        'general-telehealth': 'General Telehealth',
        'clinical-telehealth': 'Clinical Telehealth',
        'home-care': 'Home Care',
        'followUp': 'Follow-up'
      },
      scheduleType: {
        'in-person': 'In Person',
        'telehealth': 'Telehealth',
        'standard': 'Standard'
      },
      clinicLocation: value
    };

    return formatMap[field]?.[value] || value;
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentMonth.getMonth() === today.getMonth() && 
           currentMonth.getFullYear() === today.getFullYear();
  };

  const isSelected = (day) => {
    return selectedDate === day;
  };

  // Check if a day has available slots (for green background)
  const hasAvailableSlots = (day) => {
    if (!doctorMonthAvailability || !doctorMonthAvailability.days) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    // Only show availability for future dates
    if (currentDate < today) return false;
    
    const dayData = doctorMonthAvailability.days.find(d => {
      const availDate = new Date(d.avail_date);
      availDate.setHours(0, 0, 0, 0);
      return availDate.getTime() === currentDate.getTime();
    });
    
    return dayData && dayData.is_available && dayData.available_slots_count > 0;
  };

  // Check if a day is unavailable or has no slots (for red background)
  const isUnavailableDay = (day) => {
    if (!doctorMonthAvailability || !doctorMonthAvailability.days) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    // Only show unavailability for future dates
    if (currentDate < today) return false;
    
    const dayData = doctorMonthAvailability.days.find(d => {
      const availDate = new Date(d.avail_date);
      availDate.setHours(0, 0, 0, 0);
      return availDate.getTime() === currentDate.getTime();
    });
    
    return dayData && (!dayData.is_available || dayData.available_slots_count === 0);
  };

  const handleDateSelect = (day) => {
    setSelectedDate(day);
    setSelectedTime(null);
  // Fetch available times for selected doctor + date
  const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
  fetchDoctorSlots(d);
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'next') {
        newMonth.setMonth(newMonth.getMonth() + 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() - 1);
      }
      
      // Fetch availability for the new month if doctor is selected
      if (bookingForm.doctor_id) {
        fetchDoctorMonthAvailability(newMonth);
      }
      
      return newMonth;
    });
  };

  const handleBookingFormChange = (field, value) => {
    console.log(`🔄 Patient Booking Form Change: ${field} = ${value}`);
    const updatedForm = { ...bookingForm, [field]: value };
    
    // Auto-set schedule type based on appointment type
    if (field === 'appointmentType') {
      if (value === 'Home Care') {
        updatedForm.scheduleType = 'in-person';
      } else if (value === 'General Telehealth' || value === 'Clinical Telehealth') {
        updatedForm.scheduleType = 'telehealth';
      } else {
        updatedForm.scheduleType = '';
      }
      
      console.log(`📋 Auto-setting schedule type: ${updatedForm.scheduleType} for appointment type: ${value}`);
      // Clear clinic location when switching to general telehealth
      if (value === 'general-telehealth') {
        console.log('🚫 Clearing clinic location for general telehealth');
        updatedForm.clinicLocation = '';
      } else {
        console.log('✅ Keeping clinic location for clinical telehealth');
      }
    }
    
    setBookingForm(updatedForm);
  };

  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState('');
  
  // Popup state
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Cancel popup state
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');

  // Edit popup state
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedBooking(null);
  };

  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking);
    setIsCancelPopupOpen(true);
  };

  const handleConfirmCancel = async (bookingId) => {
    setCancelLoading(true);
    setCancelMessage('');
    
    try {
      await patientService.cancelPatientBooking(bookingId);
      setCancelMessage('Appointment cancelled successfully!');
      
      // Refresh upcoming bookings to reflect the changes
      await fetchUpcomingBookings();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setCancelMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setCancelMessage('Failed to cancel appointment. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setCancelMessage('');
      }, 5000);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseCancelPopup = () => {
    setIsCancelPopupOpen(false);
    setBookingToCancel(null);
  };

  const handleGoToAppointment = (booking) => {
    console.log('📋 Patient viewing appointment details for booking:', booking.id);
    
    // Navigate to patient video call page
    navigate(`/patient/video-call/${booking.id}`, { 
      state: { 
        appointmentData: {
          booking_id: booking.id,
          patient_id: booking.patient_id,
          doctor_id: booking.doctor_id,
          appointment_type: booking.appointment_type,
          appointment_date: booking.appointment_date,
          appointment_time: booking.appointment_time,
          patient_name: booking.patient_name,
          doctor_name: booking.doctor,
          clinician_name: booking.clinician_name,
          reason: booking.reason,
          specialty: booking.specialty,
          clinic_location: booking.clinic_location,
          schedule_type: booking.schedule_type,
          status: booking.status
        }
      } 
    });
  };

  const handleEditBooking = (booking) => {
    setBookingToEdit(booking);
    setIsEditPopupOpen(true);
  };

  const handleCloseEditPopup = () => {
    setIsEditPopupOpen(false);
    setBookingToEdit(null);
  };

  const handleEditSave = async () => {
    // Refresh upcoming bookings after edit
    await fetchUpcomingBookings();
  };

  const handleConfirmBooking = async () => {
    if (!selectedDate || !selectedTime || !bookingForm.reason || !bookingForm.appointmentType) {
      return;
    }

    // Check clinic location requirement for clinical telehealth
    if (bookingForm.appointmentType === 'clinical-telehealth' && !bookingForm.clinicLocation) {
      setBookingError('Clinic location is required for Clinical Telehealth appointments');
      return;
    }

    setBookingLoading(true);
    setBookingError('');

    try {
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('authToken');

      if (!userData.id || !token) {
        throw new Error('User not authenticated. Please log in again.');
      }

      // Fetch patient data from API
      const patientResponse = await apiClient.get(`/patients/by-user/${userData.id}`);
      const patientData = patientResponse.data;
      console.log('✅ Fetched patient data:', patientData);
      
      // Validate required data
      if (!patientData.id) {
        console.error('❌ Patient data missing ID:', patientData);
        throw new Error('Invalid patient data: missing patient ID');
      }
      
      console.log('✅ Patient validation passed:', {
        user_id: userData.id,
        patient_id: patientData.id
      });

      // Format date and time
      const appointmentDate = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.toString().padStart(2, '0')}`;
      const appointmentTime = selectedTime;

      // Resolve numeric doctor_id while keeping string for UI
      let numericDoctorId = null;
      if (bookingForm.doctor_id) {
        try {
          const doctorDetail = await patientService.getDoctorById(bookingForm.doctor_id);
          numericDoctorId = doctorDetail?.id || null;
        } catch (_) {
          numericDoctorId = null;
        }
      }

      // Prepare booking data with formatted values to match UI display
      const bookingData = {
        user_id: userData.id,
        patient_id: patientData.id, // Use the actual patient ID from the API response
        reason: bookingForm.reason,
        appointment_type: formatDisplayValue('appointmentType', bookingForm.appointmentType),
        schedule_type: formatDisplayValue('scheduleType', bookingForm.scheduleType),
        specialty: bookingForm.specialty,
        doctor: bookingForm.doctor,
        doctor_id: numericDoctorId,
        clinic_location: formatDisplayValue('clinicLocation', bookingForm.clinicLocation),
        clinician_id: bookingForm.clinicianId ? parseInt(bookingForm.clinicianId) : null,
        clinician_name: bookingForm.clinicianName || null,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        status: 'Scheduled' // Use Camel Case for status
      };

      console.log('Creating booking with data:', bookingData);
      console.log('Booking form state:', bookingForm);
      console.log('Selected doctor_id:', bookingForm.doctor_id);

      // Create booking
      const newBooking = await patientService.createPatientBooking(bookingData);
      
      console.log('Booking created successfully:', newBooking);
      
      // Show success message
      setBookingSuccess(true);
      
      // Refresh upcoming bookings to show the new booking
      await fetchUpcomingBookings();
      
      // Reset form
      setSelectedDate(null);
      setSelectedTime(null);
      setBookingForm({
        reason: '',
        appointmentType: '',
        scheduleType: '',
        specialty: '',
        doctor: '',
        doctor_id: '',
        clinicLocation: '',
        clinicianId: '',
        clinicianName: ''
      });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setBookingSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error creating booking:', error);
      setBookingError(error.message || 'Failed to create booking. Please try again.');
      
      // Hide error message after 5 seconds
      setTimeout(() => {
        setBookingError('');
      }, 5000);
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="flex gap-6">
        {/* Left Column */}
        <div className="w-[22%] space-y-2">
          {/* Notifications Block */}
          <NotificationList
            notifications={contextNotifications}
            unreadCount={unreadCount}
            loading={notificationsLoading}
            onNotificationClick={setSelectedNotification}
            onMarkRead={(id) => markAsRead(id)}
            onMarkAllRead={markAllAsRead}
            onRefresh={refreshNotifications}
            headerColor="blue"
          />

          {/* Accounts Block */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg">
              <h3 className="font-semibold text-sm">Accounts</h3>
            </div>
            <div className="p-2 space-y-2">
              {accounts.map((account) => (
                <div key={account.id} className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">{account.date}</span>
                    <span className="text-sm font-medium">{account.type}</span>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800">
                    {account.icon}
                  </button>
                </div>
              ))}
              <button className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium mt-3">
                + View All
              </button>
            </div>
          </div>

          {/* FAQs Block */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg">
              <h3 className="font-semibold text-sm">Faq's</h3>
            </div>
            <div className="p-2 space-y-1">
              {faqs.map((faq, index) => (
                <div key={index} className="text-sm text-gray-700">
                  {faq}
                </div>
              ))}
              <button className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium mt-3">
                + View All
              </button>
            </div>
          </div>

          {/* Latest News Block */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg">
              <h3 className="font-semibold text-sm">Latest News</h3>
            </div>
            <div className="p-2 space-y-2">
              {news.map((item) => (
                <div key={item.id} className="flex space-x-3">
                  <div className="w-16 h-16 bg-gray-300 rounded flex-shrink-0"></div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-600 mt-1">{item.excerpt}</p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-xs">
                      + Read More
                    </a>
                  </div>
                </div>
              ))}
              <button className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
                News Page
              </button>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="w-[78%] space-y-2">
          {/* Upcoming Bookings Block */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg">
              <h3 className="font-semibold text-sm">Upcoming Bookings</h3>
            </div>
            <div className="p-2">
              {loadingUpcomingBookings ? (
                <div className="flex items-center justify-center py-4">
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-600 text-sm">Loading...</span>
                  </div>
                </div>
              ) : upcomingBookings.length === 0 ? (
                <div className="text-center py-4">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-600 text-sm font-medium">No upcoming bookings</p>
                  <p className="text-gray-500 text-xs mt-1">You have no scheduled appointments</p>
                </div>
              ) : (
                upcomingBookings.map((booking) => {
                  // Format date and time
                  const formatDate = (dateString) => {
                    const date = new Date(dateString);
                    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
                  };
                  
                  const formatTime = (timeString) => {
                    if (!timeString) return '';
                    const [hours, minutes] = timeString.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const displayHour = hour % 12 || 12;
                    return `${displayHour}:${minutes} ${ampm}`;
                  };

                  return (
                    <div key={booking.id} className="grid grid-cols-5 gap-4 items-center border-b border-gray-200 pb-4">
                      <div className="text-center">
                        <div className="text-sm font-medium">{formatDate(booking.appointment_date)}</div>
                        <div className="text-xs text-gray-600">{formatTime(booking.appointment_time)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium">{booking.doctor || 'N/A'}</div>
                        <div className="text-xs text-gray-600">{booking.appointment_type || 'N/A'}</div>
                      </div>
                      <div className="text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : booking.status === 'cancelled' || booking.status === 'Cancelled'
                            ? 'bg-red-500 text-white'
                            : booking.status === 'Scheduled'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {booking.status === 'completed' ? 'Completed' : 
                           booking.status === 'cancelled' || booking.status === 'Cancelled' ? 'Cancelled' : 
                           booking.status}
                        </span>
                      </div>
                      <div className="text-center flex justify-center">
                        <button 
                          onClick={() => handleViewBooking(booking)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors duration-200 flex items-center justify-center space-x-1"
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>View</span>
                        </button>
                      </div>
                      <div className="flex flex-col space-y-1">
                        <button 
                          onClick={() => handleEditBooking(booking)}
                          className="w-full bg-blue-100 text-blue-800 px-3 py-1 rounded text-xs hover:bg-blue-200 transition-colors duration-200 flex items-center justify-center space-x-1"
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                        <button 
                          onClick={() => handleCancelBooking(booking)}
                          className="w-full bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition-colors duration-200 flex items-center justify-center space-x-1"
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Cancel</span>
                        </button>
                        <button 
                          onClick={() => handleGoToAppointment(booking)}
                          className="w-full bg-green-100 text-green-800 px-3 py-1 rounded text-xs hover:bg-green-200 transition-colors duration-200 flex items-center justify-center space-x-1"
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Go to Appointment</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Make A Booking Block */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg">
              <h3 className="font-semibold text-sm">Make A Booking</h3>
            </div>
            <div className="p-2">
              {/* Success and Error Messages */}
              {bookingSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Booking created successfully!</span>
                  </div>
                </div>
              )}
              
              {bookingError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">{bookingError}</span>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {/* Standard Appointment Section - Only for Standard patients or when patient type not loaded */}
                {(!patientData || patientData.patient_type === 'Standard') && (
                  <div className="bg-blue-50 p-3 rounded-md">
                    <h4 className="font-medium text-blue-600 mb-3">Appointment</h4>
                  
                  {/* First Row: Reason (75%) and Appointment Type (25%) */}
                  <div className="flex gap-3 mb-3">
                    <div className="w-[75%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason For Appointment
                      </label>
                      <input
                        type="text"
                        value={bookingForm.reason}
                        onChange={(e) => handleBookingFormChange('reason', e.target.value)}
                        placeholder="Please describe reason for appointment e.g. Flu symptoms."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="w-[25%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Appointment Type
                      </label>
                      <select
                        value={bookingForm.appointmentType}
                        onChange={(e) => handleBookingFormChange('appointmentType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Please Select</option>
                        <option value="General Telehealth">General Telehealth</option>
                        <option value="Clinical Telehealth">Clinical Telehealth</option>
                        <option value="Home Care" disabled={patientData?.patient_type === 'Standard'}>
                          Home Care {patientData?.patient_type === 'Standard' && '(Aged Care Only)'}
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Second Row: Schedule Type (20%), Specialty (20%), Doctor (30%), Clinic Location (30%) */}
                  <div className="flex gap-3">
                    <div className="w-[20%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Schedule Type
                      </label>
                      <select
                        value={bookingForm.scheduleType}
                        onChange={(e) => handleBookingFormChange('scheduleType', e.target.value)}
                        disabled={bookingForm.appointmentType !== ''}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          bookingForm.appointmentType !== '' ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="">Please Select</option>
                        <option value="in-person">In Person</option>
                        <option value="telehealth">Telehealth</option>
                      </select>
                    </div>
                    <div className="w-[20%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specialty
                      </label>
                      <div className="relative">
                        <select
                          value={bookingForm.specialty}
                          onChange={(e) => handleSpecialtyChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Please Select</option>
                          {loadingSpecialties ? (
                            <option value="">Loading specialties...</option>
                          ) : (
                            specialties.map((specialty) => (
                              <option key={specialty} value={specialty}>
                                {specialty}
                              </option>
                            ))
                          )}
                        </select>

                      </div>
                    </div>
                    <div className="w-[30%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Doctor
                      </label>
                      <div className="relative doctor-dropdown-container">
                        <input
                          type="text"
                          value={bookingForm.doctor}
                          readOnly
                          placeholder={loadingDoctors ? "Loading doctors..." : "Select a specialty first"}
                          className="w-full px-3 py-1.5 pr-16 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button 
                          onClick={toggleDoctorDropdown}
                          disabled={doctors.length === 0}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Change
                        </button>
                        
                        {/* Doctor Dropdown */}
                        {showDoctorDropdown && doctors.length > 0 && (
                          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {doctors.map((doctor) => (
                              <div
                                key={doctor.id}
                                className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-100 last:border-b-0"
                                onClick={() => handleDoctorSelect(doctor)}
                              >
                                <div className="font-medium">{doctor.firstname} {doctor.lastname}</div>
                                <div className="text-xs text-gray-600">{doctor.specialties}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-[30%]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Clinic Location
                      </label>
                      <div className="relative clinic-dropdown-container">
                        <input
                          type="text"
                          value={bookingForm.clinicLocation}
                          readOnly={bookingForm.appointmentType === 'General Telehealth'}
                          onClick={bookingForm.appointmentType === 'Clinical Telehealth' ? toggleClinicDropdown : undefined}
                          placeholder={
                            bookingForm.appointmentType === 'General Telehealth' 
                              ? "Not required for General Telehealth" 
                              : loadingClinics 
                                ? "Loading clinics..." 
                                : "Select clinic"
                          }
                          className={`w-full px-3 py-1.5 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            bookingForm.appointmentType === 'General Telehealth' 
                              ? 'bg-gray-100 cursor-not-allowed' 
                              : 'bg-gray-50 cursor-pointer'
                          }`}
                        />
                        <button 
                          onClick={toggleClinicDropdown}
                          disabled={availableClinics.length === 0 || bookingForm.appointmentType === 'General Telehealth'}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Change
                        </button>
                        
                        {/* Clinic Dropdown */}
                        {showClinicDropdown && availableClinics.length > 0 && (
                          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {availableClinics.map((clinic) => (
                              <div
                                key={clinic.clinic_id}
                                className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-100 last:border-b-0"
                                onClick={() => handleClinicSelect(clinic)}
                              >
                                <div className="font-medium">{clinic.clinic_name}</div>
                                <div className="text-xs text-gray-600">{clinic.clinic_address}</div>
                                {clinic.is_primary_clinic && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    Primary
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Clinician Select Field - Only show for Clinical Telehealth and when clinic is selected */}
                  {bookingForm.clinicLocation && bookingForm.appointmentType === 'Clinical Telehealth' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Clinician <span className="text-gray-500">(Optional)</span>
                      </label>
                      <select
                        value={bookingForm.clinicianId}
                        onChange={(e) => handleClinicianSelect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={loadingClinicians || clinicians.length === 0}
                      >
                        <option value="">No Specific Clinician (Any Available)</option>
                        {loadingClinicians ? (
                          <option disabled>Loading clinicians...</option>
                        ) : clinicians.length === 0 ? (
                          <option disabled>No clinicians available at this clinic</option>
                        ) : (
                          clinicians.map((clinician) => (
                            <option key={clinician.id} value={clinician.id}>
                              {clinician.full_name} {clinician.is_primary_clinic && '★ Primary'}
                            </option>
                          ))
                        )}
                      </select>
                      {clinicians.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {clinicians.length} clinician{clinicians.length !== 1 ? 's' : ''} available at {bookingForm.clinicLocation}
                        </p>
                      )}
                    </div>
                  )}
                  </div>
                )}

                {/* Home Care Booking Section - Only for Aged Care patients */}
                {patientData?.patient_type === 'Aged Care' && (
                  <HomeCareBookingSection
                    patientData={patientData}
                    onBookingSuccess={async (response) => {
                      console.log('✅ Home Care bookings created:', response);
                      toast.success(response.message);
                      
                      // Refresh upcoming bookings to show new bookings
                      await fetchUpcomingBookings();
                    }}
                  />
                )}

                {/* Month, Time and Confirmation Section - Only for Standard patients */}
                {(!patientData || patientData.patient_type === 'Standard') && (
                  <div className="bg-blue-50 p-3 rounded-md">
                  <div className="flex gap-4">
                    {/* Month/Calendar Section - 35% */}
                    <div className="w-[35%]">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-blue-600">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h4>
                        <div className="flex space-x-2">
                          <button 
                            className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-100"
                            onClick={() => handleMonthChange('prev')}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <button 
                            className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-100"
                            onClick={() => handleMonthChange('next')}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1 text-xs mb-2">
                        <div className="text-center font-medium text-gray-600">Sun</div>
                        <div className="text-center font-medium text-gray-600">Mon</div>
                        <div className="text-center font-medium text-gray-600">Tue</div>
                        <div className="text-center font-medium text-gray-600">Wed</div>
                        <div className="text-center font-medium text-gray-600">Thu</div>
                        <div className="text-center font-medium text-gray-600">Fri</div>
                        <div className="text-center font-medium text-gray-600">Sat</div>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1">
                        {/* Empty cells for days before the first day of the month */}
                        {Array.from({ length: getFirstDayOfMonth(currentMonth) }, (_, i) => (
                          <div key={`empty-${i}`} className="h-8"></div>
                        ))}
                        
                        {/* Days of the month */}
                        {Array.from({ length: getDaysInMonth(currentMonth) }, (_, i) => {
                          const day = i + 1;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0); // Reset time to start of day
                          const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                          currentDate.setHours(0, 0, 0, 0); // Reset time to start of day
                          const isPastDate = currentDate < today;
                          
                          // Determine background color based on doctor availability
                          const hasSlots = hasAvailableSlots(day);
                          const isUnavailable = isUnavailableDay(day);
                          const showColorCoding = bookingForm.doctor_id && !isPastDate;
                          
                          return (
                            <div
                              key={day}
                              className={`h-8 border rounded flex items-center justify-center text-xs cursor-pointer transition-all duration-200 hover:scale-105 ${
                                isPastDate
                                  ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                                  : isSelected(day)
                                    ? 'bg-blue-500 text-white border-blue-500 shadow-lg transform scale-105'
                                    : isToday(day) && showColorCoding
                                      ? hasSlots
                                        ? 'bg-green-400 text-gray-800 border-green-400 shadow-md hover:bg-green-500'
                                        : isUnavailable
                                          ? 'bg-red-400 text-gray-800 border-red-300 shadow-md hover:bg-red-500'
                                          : 'bg-green-400 text-gray-800 border-green-300 hover:bg-green-500'
                                      : showColorCoding
                                        ? hasSlots
                                          ? 'bg-green-400 text-gray-800 border-green-300 hover:bg-green-500 shadow-sm'
                                          : isUnavailable
                                            ? 'bg-red-400 text-gray-800 border-red-300 hover:bg-red-500 shadow-sm'
                                            : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-100'
                                        : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-100'
                              }`}
                              onClick={() => !isPastDate && handleDateSelect(day)}
                            >
                              {day}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Calendar Legend (only show when doctor is selected) */}
                      {bookingForm.doctor_id && (
                        <div className="mt-3 px-1">
                          <div className="flex justify-center space-x-3 text-xs">
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-green-400 rounded"></div>
                              <span className="text-gray-600">Available</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-red-400 rounded"></div>
                              <span className="text-gray-600">Unavailable</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-3 h-3 bg-blue-500 rounded"></div>
                              <span className="text-gray-600">Selected</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Time Section - 25% */}
                    <div className="w-[25%]">
                      <h4 className="font-medium text-blue-600 mb-3">Time</h4>
                      <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-2">
                        {!selectedDate ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-gray-500 text-sm">
                            Please select a date first
                          </div>
                        ) : !bookingForm.doctor_id ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-gray-500 text-sm">
                            Please select a doctor first
                          </div>
                        ) : availableTimes.length === 0 ? (
                          <div className="col-span-2 flex items-center justify-center p-4 text-center">
                            <div className="text-red-500 text-sm">
                              <div className="mb-1">No available slots</div>
                              <div className="text-xs text-gray-500">
                                Please select a different date
                              </div>
                            </div>
                          </div>
                        ) : (
                          availableTimes.map((time) => (
                            <div
                              key={time}
                              className={`h-8 border border-blue-200 rounded flex items-center justify-center text-xs cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md ${
                                selectedTime === time 
                                  ? 'bg-blue-500 text-white shadow-lg transform scale-105' 
                                  : 'bg-white text-gray-700 hover:bg-blue-100'
                              }`}
                              onClick={() => setSelectedTime(time)}
                            >
                              {formatTimeSlot(time)}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Confirmation Section - 40% */}
                    <div className="w-[40%] flex flex-col h-full">
                      <h4 className="font-medium text-blue-600 mb-2">Confirmation</h4>
                      <p className="text-blue-500 text-sm mb-3">Please ensure all details are correct.</p>
                      
                      <div className="flex-1 flex flex-col">
                        <div className="space-y-3 mb-3 flex-1">
                          {/* Row 1: Reason for Appointment (100%) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Reason For Appointment
                            </label>
                            <input
                              type="text"
                              value={bookingForm.reason}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>
                          
                          {/* Row 2: Appointment Type (50%) | Schedule Type (50%) */}
                          <div className="flex gap-3">
                            <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Appointment Type
                              </label>
                              <input
                                type="text"
                                value={formatDisplayValue('appointmentType', bookingForm.appointmentType)}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                              />
                            </div>
                            <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Schedule Type
                              </label>
                              <input
                                type="text"
                                value={formatDisplayValue('scheduleType', bookingForm.scheduleType)}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                              />
                            </div>
                          </div>
                          
                          {/* Row 3: Specialty (50%) | Doctor (50%) */}
                          <div className="flex gap-3">
                            <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Specialty
                              </label>
                              <input
                                type="text"
                                value={bookingForm.specialty}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                              />
                            </div>
                            <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Doctor
                              </label>
                              <input
                                type="text"
                                value={bookingForm.doctor}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                              />
                            </div>
                          </div>
                          
                          {/* Row 4: Clinic Location (100%) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Clinic Location
                            </label>
                            <input
                              type="text"
                              value={formatDisplayValue('clinicLocation', bookingForm.clinicLocation)}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>
                          
                          {/* Row 5: Appointment Date (50%) | Appointment Time (50%) */}
                          <div className="flex gap-3">
                            <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Appointment Date
                              </label>
                                                          <input
                              type="text"
                              value={selectedDate ? `${selectedDate}/${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}/${currentMonth.getFullYear()}` : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                            </div>
                            <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Appointment Time
                              </label>
                              <input
                                type="text"
                                value={selectedTime || ''}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 mb-4">
                        <input
                          type="checkbox"
                          id="terms"
                          className="text-blue-600"
                        />
                        <label htmlFor="terms" className="text-sm">
                          I understand & agree to the booking Terms & Conditions{' '}
                          <a href="#" className="text-blue-600 hover:text-blue-800">[View Here]</a>
                        </label>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={handleConfirmBooking}
                          disabled={!selectedDate || !selectedTime || !bookingForm.reason || !bookingForm.appointmentType || bookingLoading}
                          className={`px-8 py-3 border rounded-md text-sm font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100 disabled:hover:shadow-none ${
                            selectedDate && selectedTime && bookingForm.reason && bookingForm.appointmentType && !bookingLoading
                              ? 'border-blue-500 text-white bg-blue-500 hover:bg-blue-600 hover:border-blue-600'
                              : 'border-gray-300 text-gray-400 bg-gray-100'
                          }`}
                        >
                          {bookingLoading ? (
                            <span className="flex items-center space-x-2">
                              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Creating Booking...</span>
                            </span>
                          ) : selectedDate && selectedTime && bookingForm.reason && bookingForm.appointmentType ? (
                            <span className="flex items-center space-x-2">
                              <span>Confirm Booking</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          ) : (
                            <span>Complete Required Fields</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Success/Error Messages */}
      {cancelMessage && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
          cancelMessage.includes('successfully') 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {cancelMessage.includes('successfully') ? (
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{cancelMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Booking Details Popup */}
      <BookingDetailsPopup
        booking={selectedBooking}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
      />
      
      {/* Cancel Booking Popup */}
      <CancelBookingPopup
        booking={bookingToCancel}
        isOpen={isCancelPopupOpen}
        onClose={handleCloseCancelPopup}
        onConfirm={handleConfirmCancel}
      />
      
      {/* Edit Booking Popup */}
      <EditBookingPopup
        booking={bookingToEdit}
        isOpen={isEditPopupOpen}
        onClose={handleCloseEditPopup}
        onSave={handleEditSave}
      />
      
      {/* Notification Detail Dialog */}
      {selectedNotification && (
        <NotificationDetailDialog
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
          onMarkRead={markAsRead}
          onDelete={deleteNotification}
        />
      )}
    </div>
  );
};

export default PatientDashboardPage; 